import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  Divider,
  Typography,
} from "@material-ui/core";
import ArrowBackRoundedIcon from "@material-ui/icons/ArrowBackRounded";
import axios from "axios";
import { v4 as uuidv4 } from "uuid";
import { getAPIHandler, rawAPICall } from "src/Apiconfig/service";
import ApiConfig from "src/Apiconfig/config";

// rawAPICall has no request-body support, needed here to set a label right after confirming
// "mine" - mirrors the header construction the other API helpers in this file already use.
const putLabel = async (address, label) => {
  const headers = {
    "Content-Type": "application/json",
    "X-REQUEST-DATE": new Date().toISOString(),
    "X-CORRELATION-ID": uuidv4(),
    "X-REQUEST-ID": uuidv4(),
    "X-CLIENT-APP-ID": "REACT_BETA",
    Authorization: "Bearer " + window.localStorage.getItem("accessToken"),
  };
  try {
    await axios({
      method: "PUT",
      url: `${ApiConfig.transferReview}/known-addresses/${encodeURIComponent(address)}/label`,
      headers,
      data: { label },
    });
  } catch (error) {
    // Best-effort - the address is already confirmed as "mine" regardless of whether naming it worked.
  }
};
import AddressGroupsPanel from "../pages/TransferReview/AddressGroupsPanel";
import CandidateListItem from "../pages/TransferReview/CandidateListItem";
import Pagination from "../pages/TransferReview/Pagination";

const GROUPS_PAGE_SIZE = 12;
const DETAIL_PAGE_SIZE = 30;

/**
 * Shown when a sync finishes (new wallet or resync, see Wallet.js) and from the standing
 * "Which wallets are mine?" button. Reuses the Needs-Review building blocks (address-groups
 * suggestions, bulk Mine/Not-mine, manual paste box) so self-transfers get caught before they're
 * sent through AI labeling.
 *
 * Managing addresses already marked "mine" (renaming, deleting) lives permanently on the Wallets
 * page itself now (see KnownWalletsSection) rather than duplicated in here - this modal is just
 * the one-time "did you forget one?" prompt.
 *
 * Every Mine/Not-mine/manual-add action re-fetches the group list from the server instead of
 * trusting a local filter - a group can legitimately still match after a manual "mark as mine"
 * (e.g. the backend's address normalization doesn't match the group's display casing), and a
 * stale local removal would hide that mismatch instead of surfacing it.
 */
const WhichWalletsAreYoursModal = ({ open, onDone }) => {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageNumber, setPageNumber] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [detailAddress, setDetailAddress] = useState(null);
  const [detailCandidates, setDetailCandidates] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailPageNumber, setDetailPageNumber] = useState(1);
  const [detailTotalPages, setDetailTotalPages] = useState(1);

  const fetchGroups = useCallback((targetPage) => {
    setLoading(true);
    return getAPIHandler({
      endPoint: "transferReviewAddressGroups",
      paramsData: { pageNumber: targetPage, pageSize: GROUPS_PAGE_SIZE },
    })
      .then((res) => {
        const rows = res?.data?.groups || [];
        setGroups(rows);
        const total = res?.data?.pagination?.totalCount || 0;
        setTotalPages(Math.max(1, Math.ceil(total / GROUPS_PAGE_SIZE)));
        setPageNumber(targetPage);
      })
      .catch(() => {
        setGroups([]);
        setTotalPages(1);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (open) {
      fetchGroups(1);
      setDetailAddress(null);
    }
  }, [open, fetchGroups]);

  // Re-fetch from the server after every write instead of trusting a local removal - the source
  // of truth is the backend's address-groups aggregation, not whatever the client assumed happened.
  const refreshAfterAction = (targetPage = pageNumber) => {
    fetchGroups(targetPage);
  };

  const confirmGroup = async (group) => {
    const url = `${ApiConfig.transferReviewAddressGroupsConfirm}?address=${encodeURIComponent(group.address)}`;
    await rawAPICall({ method: "POST", url });
    // Ask for a name right away, while the address is still front of mind - matches the naming
    // step the manual "My Wallet Addresses" add form already has. Skippable (Cancel/blank = unnamed).
    const name = window.prompt(`Name this wallet? (optional)\n${group.address}`, "");
    if (name && name.trim()) {
      await putLabel(group.address, name.trim());
    }
    refreshAfterAction();
  };

  const dismissGroup = async (group) => {
    const url = `${ApiConfig.transferReviewAddressGroupsDismiss}?address=${encodeURIComponent(group.address)}`;
    await rawAPICall({ method: "POST", url });
    refreshAfterAction();
  };

  const fetchDetailPage = useCallback((address, targetPage) => {
    setDetailLoading(true);
    return getAPIHandler({
      endPoint: "transferReview",
      id: "candidates",
      paramsData: { pageNumber: targetPage, pageSize: DETAIL_PAGE_SIZE, counterpartyAddress: address },
    })
      .then((res) => {
        const rows = res?.data?.candidates || [];
        setDetailCandidates(rows);
        const total = res?.data?.pagination?.totalCount || 0;
        setDetailTotalPages(Math.max(1, Math.ceil(total / DETAIL_PAGE_SIZE)));
        setDetailPageNumber(targetPage);
      })
      .catch(() => {
        setDetailCandidates([]);
        setDetailTotalPages(1);
      })
      .finally(() => setDetailLoading(false));
  }, []);

  const openAddress = (group) => {
    setDetailAddress(group.address);
    fetchDetailPage(group.address, 1);
  };

  const confirmCandidate = async (candidate) => {
    await rawAPICall({ method: "POST", url: `${ApiConfig.transferReview}/${candidate.walletId}/${candidate.txnId}/confirm` });
    fetchDetailPage(detailAddress, detailPageNumber);
    refreshAfterAction();
  };

  const dismissCandidate = async (candidate) => {
    await rawAPICall({ method: "POST", url: `${ApiConfig.transferReview}/${candidate.walletId}/${candidate.txnId}/dismiss` });
    fetchDetailPage(detailAddress, detailPageNumber);
    refreshAfterAction();
  };

  return (
    <Dialog fullWidth maxWidth="sm" open={open} onClose={onDone}>
      <DialogContent>
        <Typography variant="h6" color="primary" style={{ fontWeight: 700, marginBottom: 4 }}>
          Which wallets are yours?
        </Typography>
        <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
          Transfers between your own wallets aren't trades. Tell us which addresses are yours so
          we can label them correctly, instead of sending every one through AI review.
        </Typography>

        {detailAddress ? (
          <>
            <Button
              size="small"
              onClick={() => setDetailAddress(null)}
              startIcon={<ArrowBackRoundedIcon style={{ fontSize: 16 }} />}
              style={{ textTransform: "none", marginBottom: 12, marginLeft: -8 }}
            >
              Back to addresses
            </Button>
            <Typography variant="body2" color="textSecondary" style={{ marginBottom: 12 }}>
              Transactions with <span style={{ fontFamily: "monospace", fontWeight: 600 }}>{detailAddress}</span>
            </Typography>
            {detailLoading ? (
              <Box display="flex" justifyContent="center" py={6}>
                <CircularProgress />
              </Box>
            ) : detailCandidates.length === 0 ? (
              <Typography variant="body2" color="textSecondary">Nothing left to review for this address.</Typography>
            ) : (
              <>
                {detailCandidates.map((candidate) => (
                  <CandidateListItem
                    key={`${candidate.walletId}-${candidate.txnId}`}
                    candidate={candidate}
                    resolveExchangeImage={() => null}
                    formatDateTime={(d) => { try { return new Date(d).toLocaleDateString(); } catch (e) { return "-"; } }}
                    onConfirm={() => confirmCandidate(candidate)}
                    onDismiss={() => dismissCandidate(candidate)}
                  />
                ))}
                <Pagination
                  pageNumber={detailPageNumber}
                  totalPages={detailTotalPages}
                  onPageChange={(page) => fetchDetailPage(detailAddress, page)}
                />
              </>
            )}
          </>
        ) : (
          <AddressGroupsPanel
            groups={groups}
            loading={loading}
            pageNumber={pageNumber}
            totalPages={totalPages}
            onPageChange={(page) => fetchGroups(page)}
            onMine={confirmGroup}
            onNotMine={dismissGroup}
            onReview={openAddress}
            onAddressAdded={() => refreshAfterAction(1)}
          />
        )}

        <Divider style={{ margin: "20px 0 16px" }} />
        <Box display="flex" justifyContent="flex-end">
          <Button variant="contained" color="primary" onClick={onDone} style={{ textTransform: "none" }}>
            Done
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default WhichWalletsAreYoursModal;
